import { Platform } from '../types/Platform';
import { getJson } from './Utils';

// The anonymous /try funnel, in three calls so the page can show honest progress: `start` downloads,
// each `evaluate` is one engine pass, `summary` says whether there's anything to repair.

export interface TryGame {
  Id: number;
  White: string | null;
  Black: string | null;
  Date: string;
  Result: string | null;
  TimeControl: string | null;
  Opening: string | null;
  Evaluated: boolean;
}

export interface TryStartResult {
  Handle: string;
  GroupToken: string;
  // Set when nothing usable came back (unknown handle, wrong platform, no recent games). Shown in place
  // of the progress.
  Error: string | null;
  // True when the platform has no such account (as opposed to an account with no recent games).
  UserNotFound: boolean;
  Games: TryGame[];
}

export interface TrySummary {
  Blunders: number;
  Misses: number;
  Mistakes: number;
  Inaccuracies: number;
  Total: number;
  // The shallowest depth with something in it (4 blunders … 1 inaccuracies), so the session never opens
  // on "nothing to repair".
  SuggestedMinWeight: number;
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const payload = await response.json();
      if (payload?.Error) {
        message = payload.Error;
      }
    } catch {
      /* no JSON body — keep the status line */
    }
    throw new Error(message);
  }
  return await getJson<T>(response);
}

// Pulls the visitor's newest games into a trial group. Expected failures resolve with Error set rather
// than throwing.
async function start(
  username: string,
  platform: Platform,
  maxGames?: number,
): Promise<TryStartResult> {
  return await post<TryStartResult>('/api/Try/start', {
    Username: username,
    Platform: platform,
    MaxGames: maxGames,
  });
}

// Runs the engine over one game and returns when it's finished, so the progress is real.
async function evaluate(gameId: number): Promise<void> {
  await post<{ Evaluated: boolean }>(`/api/Try/evaluate?gameId=${gameId}`, {});
}

async function summary(gameIds: number[], username: string): Promise<TrySummary> {
  return await post<TrySummary>('/api/Try/summary', { GameIds: gameIds, Username: username });
}

export default { start, evaluate, summary };
